import BeeClient from '../functions/beeClient.js';
import windowHelper from '../functions/windowHelper.js';
import Bugsnag from '@bugsnag/js';
import {difference} from 'lodash';
import dynamicContentUtils from '../components/dynamic-contents/dynamicContentUtils.js';
import {translate as t} from '../i18n';

const parseContent = dynamicContentUtils().parseContent;

const CLOSE_EDITOR = {status: 'CLOSE_EDITOR'};
const EXPECTATION_FAILED = 417;

let bee;
let resolveCondition;
let rejectCondition;
let resolveDynamicContent;
let rejectDynamicContent;
let resolveExternalContentUrls;
let rejectExternalContentUrls;

const downloadFile = (name, payload, fileType, extension) => {
  const blob = new Blob([payload], {type: fileType});
  const a = document.createElement('a');
  const date = new Date();
  let templateName;
  if (!name || /^\s*$/.test(name)) {
    templateName = 'exported-template';
  } else {
    templateName = name;
  }
  a.download = templateName + '-' + date.toISOString() + extension;
  a.href = URL.createObjectURL(blob);
  a.dataset.downloadurl = [fileType, a.download, a.href].join(':');
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(function () {
    URL.revokeObjectURL(a.href);
  }, 1500);
};

const downloadJson = (name, json) => {
  downloadFile(name, json, 'application/json', '.json');
};

const downloadHtml = (name, html) => {
  downloadFile(name, html, 'text/html', '.html');
};

const initEditor = (
  commit,
  state,
  dispatch,
  data,
  messageService,
  authSupport,
  serviceClient
) => {
  const showDisplayConditionsCallback = (resolve, reject) => {
    commit('setDisplayConditionsOpen', true);
    resolveCondition = resolve;
    rejectCondition = reject;
  };
  const showDynamicContentsCallback = (resolve, reject) => {
    commit('setDynamicContentsOpen', true);
    resolveDynamicContent = resolve;
    rejectDynamicContent = reject;
  };
  const showExternalContentUrlsCallback = (resolve, reject) => {
    commit('setExternalContentUrlsOpen', true);
    resolveExternalContentUrls = resolve;
    rejectExternalContentUrls = reject;
  };
  const getRowsCallback = async (resolve, reject, args) => {
    try {
      const response = await serviceClient.getRows(
        state.accessToken,
        state.customer,
        args.handle,
        {page: args.chunk}
      );
      resolve(response.content.map(c => JSON.parse(c.beeContent)));
    } catch (error) {
      commit('setError', error);
      reject(error);
    }
  };
  const showSaveRowDialogCallback = async (
    resolve,
    reject,
    row,
    editRowExternalDialog = false
  ) => {
    // Open a dialog box for metadata input. The callback should 'resolve'
    // with the metadata or 'reject'  if the user canceled/the metadata is
    // invalid
    await dispatch(
      'rowCategoriesModule/retrieveRowCategories',
      {},
      {root: true}
    );

    if (
      row.metadata &&
      !(await dispatch('rowsModule/rowExists', {row}, {root: true}))
    ) {
      row.metadata.id = undefined;
    }

    commit('setSaveRowOpen', true);
    commit('setSaveRowPromise', {resolve, reject});
    commit('setSaveRowData', row);
    commit('setEditRowExternalDialog', editRowExternalDialog);
  };
  const deleteRowDialogCallback = async (resolve, reject, args) => {
    const rowId = args?.row?.metadata?.id;
    try {
      await serviceClient.deleteRow(state.accessToken, state.customer, rowId);
      resolve(true);
    } catch (error) {
      commit('setError', error);
      reject(false);
    }
  };
  commit('setTemplateData', data);
  commit('setCurrentTemplate', data.template);
  bee.initialize(
    data,
    showDisplayConditionsCallback,
    showDynamicContentsCallback,
    showExternalContentUrlsCallback,
    showSaveRowDialogCallback,
    getRowsCallback,
    deleteRowDialogCallback
  );
  bee.setSaveAction((jsonFile, htmlFile) => {
    commit('setCurrentTemplate', jsonFile);
    commit('setCurrentHTMLTemplate', htmlFile);
    commit('setOpenSaveModal', true);
  });
  bee.setUseAction((jsonFile, html) => {
    commit('setCurrentTemplate', jsonFile);
    commit('setCurrentHTMLTemplate', html);
    commit('setShowUseModal', false);
    const templatePayload = {
      json: state.dirtyTemplate,
      html: state.dirtyTemplateHTML
    };
    if (state.templateId != null) {
      templatePayload.templateId = state.templateId;
    }
    messageService.post({
      status: 'EXPORT_TEMPLATE',
      payload: templatePayload
    });
  });
  bee.setExportAction((jsonFile, htmlFile) => {
    commit('setCurrentTemplate', jsonFile);
    commit('setCurrentHTMLTemplate', htmlFile);
    downloadHtml(state.templateName, htmlFile);
  });
  bee.setTrackChanges(jsonFile => {
    commit('setCurrentTemplate', jsonFile);
    authSupport.keepAlive(state.accessToken);
  });
  bee.setSendAction(html => {
    commit('setHtmlTest', html);
    commit('setShowTestModal', true);
  });
};

export default (authSupport, serviceClient, messageService) => {
  bee = BeeClient();
  return {
    closeEditor() {
      messageService.post(CLOSE_EDITOR);
    },
    async loadCustomerInfo({state, commit}) {
      try {
        const data = await serviceClient.customerInfo(
          state.accessToken,
          state.customer
        );
        if (data.dynamicContent) {
          commit('setDynamicContents', true);
        }
      } catch (error) {
        commit('setError', error);
      }
    },
    async retrieveLibraries({state, commit, dispatch}) {
      try {
        commit('setLoadingLibraries', true);
        const data = await serviceClient.getLibraries(
          state.accessToken,
          state.customer
        );
        commit('setLibraries', data.libraries);
        await dispatch('changeLibrary', data.libraries[0].id);
      } catch (error) {
        commit('setError', error);
      } finally {
        commit('setLoadingLibraries', false);
      }
    },
    async changeLibrary({commit, state}, libraryId) {
      commit('activateLibrary', libraryId);
      try {
        commit('setLoadingTemplates', true);
        commit('setSelectedTags', []);
        const data = await serviceClient.getTemplates(
          state.accessToken,
          state.customer,
          libraryId,
          state.currentPage
        );
        commit('setTemplates', data);
        commit('setDestinationLibrary', libraryId);
      } catch (error) {
        commit('setError', error);
        commit('setTemplates', {data: []});
      } finally {
        commit('setLoadingTemplates', false);
        commit('setFilterTemplateName', '');
      }
    },
    async getTags({state, commit}) {
      try {
        const customerTags = await serviceClient.getTags(
          state.accessToken,
          state.customer
        );
        commit('setCustomerTags', customerTags.tags);
      } catch (error) {
        commit('setError', error);
      }
    },
    async updateTags({state, dispatch, commit}, {tags, updateStatus = true}) {
      const deletedTags = difference(state.customerTags, tags);
      if (deletedTags.length === 0) {
        dispatch('saveTags', {tags, updateStatus});
      } else {
        commit('setPendingTags', tags);
        commit('setDeleteTagsModal', true);
      }
    },
    async saveTags({state, commit}, {tags, updateStatus = true}) {
      try {
        const customerTags = await serviceClient.saveTags(
          state.accessToken,
          state.customer,
          tags
        );
        commit('setCustomerTags', customerTags.tags);
        if (updateStatus) {
          commit('setSuccess', true);
        }
      } catch (error) {
        commit('setError', error);
      }
    },
    async selectPage({commit, state, getters}, page) {
      commit('setPage', page);
      try {
        commit('setLoadingTemplates', true);
        const filters = {
          name: state.filterTemplateName,
          selectedTags: state.selectedTags
        };
        const data = await serviceClient.getTemplates(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          page,
          filters
        );
        commit('setTemplates', data);
      } catch (error) {
        commit('setError', error);
        commit('setTemplates', {data: []});
      } finally {
        commit('setLoadingTemplates', false);
      }
    },
    async initCustomTemplate({commit, state, dispatch}, initTemplate) {
      if (initTemplate !== undefined) {
        try {
          commit('setLoadingTemplate', true);
          const data = await serviceClient.getTemplate(
            state.accessToken,
            state.customer,
            'CUSTOM',
            0,
            state.newsletter
          );
          data.template = initTemplate;

          initEditor(
            commit,
            state,
            dispatch,
            data,
            messageService,
            authSupport,
            serviceClient
          );

          commit('openEditor');
          bee.start();
        } catch (error) {
          commit('setError', error);
        } finally {
          commit('setLoadingTemplate', false);
        }
      }
    },
    async downloadTemplateJson({commit, state, getters}, {id, name}) {
      try {
        commit('setLoadingTemplate', true);
        const data = await serviceClient.getTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          id,
          state.newsletter
        );
        downloadJson(name, data.template);
      } catch (error) {
        await handleMissingUserdb(commit, error);
      } finally {
        commit('setLoadingTemplate', false);
      }
    },
    async downloadTemplate({commit, state, getters}, {id, name}) {
      try {
        commit('setLoadingTemplate', true);
        const data = await serviceClient.downloadHtmlTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          id
        );

        downloadHtml(name, data);
      } catch (error) {
        await handleMissingUserdb(commit, error);
      } finally {
        commit('setLoadingTemplate', false);
      }
    },
    async retrieveTemplate({commit, state, dispatch, getters}, {id, name}) {
      try {
        commit('setLoadingTemplate', true);

        const data = await serviceClient.getTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          id,
          state.newsletter
        );

        initEditor(
          commit,
          state,
          dispatch,
          data,
          messageService,
          authSupport,
          serviceClient
        );

        commit('setTemplateName', data.name);
        commit('setTemplateId', data.id);
        commit('setMergeTagsSource', data.mergeTagsSource);
        commit('toggleEditor');
        bee.start();
      } catch (error) {
        await handleMissingUserdb(commit, error);
      } finally {
        commit('setLoadingTemplate', false);
      }
    },
    async useTemplate() {
      bee.retrieveTemplate();
    },
    async exportTemplate() {
      bee.exportTemplate();
    },
    async backToWork({commit, state}) {
      commit('toggleEditor');
      bee.continue(state.dirtyTemplate);
    },
    async saveTemplate({commit, state, dispatch, getters}) {
      const destinationLibrary = getters.canSelectDestinationLibrary
        ? state.destinationLibrary
        : state.libraries[0].id;
      if (!state.dirtyTemplate) {
        Bugsnag.notify(new Error('Trying to save an invalid template'));
      }
      try {
        commit('setSavingTemplate', true);
        let savedTemplate = await serviceClient.saveTemplate(
          state.accessToken,
          state.customer,
          destinationLibrary,
          state.templateName,
          state.dirtyTemplate,
          state.dirtyTemplateHTML,
          state.mergeTagsSource
        );
        savedTemplate = await serviceClient.getTemplate(
          state.accessToken,
          state.customer,
          destinationLibrary,
          savedTemplate.id
        );
        commit('setTemplateData', savedTemplate);
        commit('setSuccess', true);
        commit('setCurrentTemplate', '');
        commit('incrementLibrarySize', destinationLibrary);
        dispatch('selectPage', state.currentPage);
      } catch (error) {
        await dispatch('handleConflict', {
          error,
          jsonFile: state.dirtyTemplate
        });
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    // First assumption a template can be duplicated only inside his library
    async duplicateTemplate(
      {commit, state, dispatch, getters},
      {originalTemplate, overwrite}
    ) {
      const currentLibrary = getters.activeLibrary;
      try {
        commit('setSavingTemplate', true);
        const templateToCopy = await serviceClient.getTemplate(
          state.accessToken,
          state.customer,
          currentLibrary.id,
          originalTemplate
        );
        await serviceClient.saveTemplate(
          state.accessToken,
          state.customer,
          currentLibrary.id,
          'copy-of-' + templateToCopy.name,
          templateToCopy.template,
          templateToCopy.htmlTemplate,
          templateToCopy.mergeTagsSource
        );

        commit('setSuccess', true);
        commit('incrementLibrarySize', currentLibrary.id);
        dispatch('selectPage', state.currentPage);
      } catch (error) {
        await dispatch('handleConflict', {
          error,
          overwrite,
          originalTemplate
        });
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    async saveTemplateTags({commit, state, getters}, template) {
      try {
        commit('setSavingTemplate', true);
        const templateToUpdate = await serviceClient.getTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          template.id
        );

        await serviceClient.updateTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          template.id,
          templateToUpdate.name,
          templateToUpdate.template,
          templateToUpdate.htmlTemplate,
          templateToUpdate.mergeTagsSource,
          template.tags
        );

        commit('setSuccess', true);
      } catch (error) {
        await handleMissingUserdb(commit, error);
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    async updateTemplate({commit, state}, {templateName, templateId}) {
      const templateData = state.templateData || {};
      if (!state.dirtyTemplate) {
        Bugsnag.notify(
          new Error('Trying to update a template with invalid data')
        );
      }
      try {
        commit('setSavingTemplate', true);
        const data = await serviceClient.updateTemplate(
          state.accessToken,
          state.customer,
          state.destinationLibrary,
          templateId,
          templateName,
          state.dirtyTemplate,
          state.dirtyTemplateHTML,
          templateData.mergeTagsSource
        );
        commit('setTemplateId', data.id);
        commit('updateTemplate', data);
        commit('setSuccess', true);
        commit('setCurrentTemplate', '');
      } catch (error) {
        commit('setError', error);
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    async handleConflict(
      {commit, state, dispatch, getters},
      {error, overwrite, originalTemplate}
    ) {
      // If the error is not a conflict no need to proceed
      if (error.status !== 409) {
        await handleMissingUserdb(commit, error);
        return;
      }

      const templateName = state.templateName;
      const templateId = state.templateId;

      try {
        const conflict = error.details.find(
          element => element.code === '300102'
        );
        // if the conflict is on the same template no further action is required
        if (conflict.data.conflictingTemplateUid === templateId) {
          await dispatch('updateTemplate', {templateName, templateId});
        } else {
          if (overwrite) {
            const templateToCopy = await serviceClient.getTemplate(
              state.accessToken,
              state.customer,
              getters.activeLibrary.id,
              originalTemplate
            );
            await serviceClient.updateTemplate(
              state.accessToken,
              state.customer,
              state.destinationLibrary,
              conflict.data.conflictingTemplateUid,
              conflict.data.name,
              templateToCopy.template,
              templateToCopy.htmlTemplate
            );
            commit('setSuccess', true);
          } else {
            commit('setConflict', {
              status: true,
              id: conflict.data.conflictingTemplateUid,
              name: conflict.data.name
            });
          }
        }
      } catch (error) {
        commit('setError', error);
      }
    },
    async deleteTemplate({commit, state, dispatch, getters}, {id}) {
      try {
        commit('setSavingTemplate', true);
        await serviceClient.deleteTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          id
        );
        commit('setSuccess', true);
        commit('decrementLibrarySize', getters.activeLibrary.id);
        dispatch('selectPage', state.currentPage);
      } catch (error) {
        commit('setError', error);
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    async doRenameTemplate(
      {commit, state, dispatch, getters},
      {originalTemplate, newTemplateName}
    ) {
      try {
        if (originalTemplate.name !== newTemplateName) {
          commit('setSavingTemplate', true);
          await serviceClient.renameTemplate(
            state.accessToken,
            state.customer,
            getters.activeLibrary.id,
            originalTemplate.id,
            newTemplateName
          );
          commit('setSuccess', true);
          dispatch('selectPage', state.currentPage);
        }
      } catch (error) {
        dispatch('handleRenameError', error);
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    // TODO questo può diventare una mutations
    async handleRenameError({commit}, error) {
      if (error.type !== 'conflict') {
        commit('setError', error);
        return;
      }
      commit('setConflict', {
        status: true,
        kind: 'rename',
        id: error.data.id,
        name: error.data.name
      });
    },
    async checkUserAccess({state, commit}) {
      try {
        if (state.accessToken !== undefined) {
          return;
        }

        const accessToken = windowHelper.getAccessTokenFromHash();
        if (accessToken === undefined) {
          windowHelper.redirectToOauth();
          return;
        }

        const userInformation = await authSupport.getUserInformation(
          accessToken.access_token
        );
        const roles = userInformation.roles[process.env.VUE_APP_ROLE_ID] || [];

        const isCustomerCare = Object.values(userInformation.roles)
          .flat(1)
          .includes('CUSTOMER_CARE');

        const authorized =
          roles.find(r => r === 'ED_USER' || r === 'ED_CLAB_OPERATOR') || false;
        if (authorized) {
          commit('saveCredentials', accessToken.access_token);
          commit('setIsCustomerCare', isCustomerCare);
        } else {
          commit('clearCredentials');
        }
      } catch (error) {
        commit('clearCredentials');
      }
    },
    async doShareTemplate({commit, state, dispatch, getters}, {id, shared}) {
      try {
        commit('setSavingTemplate', true);
        await serviceClient.shareTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          id,
          !shared
        );
        if (!shared) {
          commit('setCustomMessage', t('modals.share_custom_message'));
        }
        commit('setSuccess', true);
        dispatch('selectPage', state.currentPage);
      } catch (error) {
        commit('setError', error);
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    async retrieveMergeTagsSources({commit, state}, {page = 0} = {}) {
      await loadMergeTagsSource(commit, state, serviceClient, page);
    },
    async assignMergeTagsSource(
      {commit, state, getters, dispatch},
      {templateId, mergeTagsSource}
    ) {
      try {
        commit('setSavingTemplate', true);
        await serviceClient.assignMergeTagsSourceToTemplate(
          state.accessToken,
          state.customer,
          getters.activeLibrary.id,
          templateId,
          mergeTagsSource
        );
        commit('setSuccess', true);
        dispatch('selectPage', state.currentPage);
      } catch (error) {
        commit('setError', error);
      } finally {
        commit('setSavingTemplate', false);
      }
    },
    async resolveDisplayCondition(
      {commit, state},
      {mergeTagsSource, condition}
    ) {
      try {
        const result = await serviceClient.parseDisplayCondition(
          state.accessToken,
          state.customer,
          mergeTagsSource,
          condition
        );
        const payload = {
          type: '',
          label: result.name,
          description: '',
          before: result.before,
          after: result.after
        };
        resolveCondition(payload);
      } catch (error) {
        commit('setError', error);
        rejectCondition();
      }
    },
    async retrieveDisplayCondition(
      {commit, state},
      {mergeTagsSource, conditionId}
    ) {
      try {
        const result = await serviceClient.getDisplayCondition(
          state.accessToken,
          state.customer,
          mergeTagsSource,
          conditionId
        );
        const payload = {
          type: '',
          label: result.name,
          description: '',
          before: result.before,
          after: result.after
        };
        resolveCondition(payload);
      } catch (error) {
        commit('setError', error);
        rejectCondition();
      }
    },
    async getDisplayCondition({commit, state}, {mergeTagsSource, conditionId}) {
      try {
        return await serviceClient.getDisplayCondition(
          state.accessToken,
          state.customer,
          mergeTagsSource,
          conditionId
        );
      } catch (error) {
        commit('setError', error);
        if (rejectCondition !== undefined) {
          rejectCondition();
        }
      }
    },
    async rejectDisplayCondition({commit, state}) {
      if (rejectCondition !== undefined) {
        rejectCondition();
      }
    },
    async resolveDynamicContent({commit, state}, payload) {
      try {
        const dynamicContent = parseContent(payload.savedContent);
        resolveDynamicContent(dynamicContent);
      } catch (error) {
        commit('setError', error);
        rejectDynamicContent();
      }
    },
    async rejectDynamicContent({commit, state}) {
      rejectDynamicContent();
    },
    async resolveExternalContentUrls() {
      try {
        resolveExternalContentUrls();
      } catch (error) {
        if (rejectExternalContentUrls !== undefined) {
          rejectExternalContentUrls();
        }
      }
    },
    async rejectExternalContentUrls() {
      if (rejectExternalContentUrls !== undefined) {
        rejectExternalContentUrls();
      }
    },
    resetExternalContentUrls(_, {categories}) {
      bee.resetExternalContentUrls(categories);
    }
  };
};

async function loadMergeTagsSource(commit, state, serviceClient, page) {
  try {
    const response = await serviceClient.mergeTagsSources(
      state.accessToken,
      state.customer,
      {page}
    );
    commit('appendMergeTagsSources', response.content);
    if (response.last === false) {
      await loadMergeTagsSource(commit, state, serviceClient, page + 1);
    }
  } catch (error) {
    commit('setError', error);
  }
}

async function handleMissingUserdb(commit, error) {
  if (error.status === EXPECTATION_FAILED) {
    const detail = {
      message: t('errors.missing_source')
    };
    error.details = error.details || [];
    error.details.push(detail);
    commit('setWarning', error);
  } else {
    commit('setError', error);
  }
}
